import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/PricingTable.graphql';
import { Query, QuerypricingTableArgs } from '../../__generated__/globalTypes';

export const usePricingTableQuery = (options?: QueryHookOptions<Query, QuerypricingTableArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerypricingTableArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const usePricingTableLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerypricingTableArgs>
): QueryResult<Query, QuerypricingTableArgs> & {
  getPricingTable: LazyQueryExecFunction<Query, QuerypricingTableArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getPricingTable, result] = useLazyQuery<Query, QuerypricingTableArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getPricingTable, ...result };
};

export const usePricingTablePromiseQuery = () => {
  const client = useApolloClient();

  const getPricingTable = useCallback(
    (options?: Omit<QueryOptions<QuerypricingTableArgs, Query>, 'query'>) =>
      client.query<Query, QuerypricingTableArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getPricingTable };
};
// Suspense Query Hooks
export const usePricingTableSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QuerypricingTableArgs>) => {
  const result = useSuspenseQuery<Query, QuerypricingTableArgs>(QUERY, {
    ...options
  });

  return result;
};
